import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useCart } from '../contexts/CartContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Separator } from '../components/ui/separator';
import { Minus, Plus, Trash2, ShoppingBag, ArrowRight, Loader2 } from 'lucide-react';

export const CartPage = () => {
  const navigate = useNavigate();
  const { items, total, loading, updateQuantity, removeFromCart } = useCart();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center py-12">
        <ShoppingBag className="h-16 w-16 text-muted-foreground mb-4" strokeWidth={1} />
        <h2 className="font-heading text-2xl font-semibold mb-2">Your cart is empty</h2>
        <p className="text-muted-foreground mb-6">Looks like you haven't added any books yet.</p>
        <Button asChild className="btn-lift">
          <Link to="/browse">Browse Books</Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-12">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <h1 className="font-heading text-4xl font-bold mb-8">Shopping Cart</h1>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-4">
            {items.map((item) => (
              <Card key={item.cart_item_id} className="border-border" data-testid={`cart-item-${item.book_id}`}>
                <CardContent className="p-4">
                  <div className="flex gap-4">
                    <Link to={`/book/${item.book_id}`} className="shrink-0">
                      <img
                        src={item.cover_image}
                        alt={item.title}
                        className="w-20 h-28 object-cover rounded-sm img-matte"
                      />
                    </Link>
                    <div className="flex-1 min-w-0">
                      <Link to={`/book/${item.book_id}`}>
                        <h3 className="font-heading text-lg font-semibold truncate hover:text-primary transition-colors">
                          {item.title}
                        </h3>
                      </Link>
                      <p className="text-sm text-muted-foreground mb-2">{item.author}</p>
                      <p className="font-semibold text-primary">${item.price.toFixed(2)}</p>
                    </div>
                    <div className="flex flex-col items-end justify-between">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => removeFromCart(item.cart_item_id)}
                        className="text-muted-foreground hover:text-destructive"
                        data-testid={`remove-item-${item.book_id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => updateQuantity(item.cart_item_id, item.quantity - 1)}
                          disabled={item.quantity <= 1}
                        >
                          <Minus className="h-3 w-3" />
                        </Button>
                        <span className="w-8 text-center font-medium">{item.quantity}</span>
                        <Button
                          variant="outline"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => updateQuantity(item.cart_item_id, item.quantity + 1)}
                        >
                          <Plus className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Order Summary */}
          <div>
            <Card className="border-border sticky top-24">
              <CardContent className="p-6">
                <h2 className="font-heading text-xl font-semibold mb-4">Order Summary</h2>
                
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Subtotal</span>
                    <span>${total.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Shipping</span>
                    <span>{total >= 30 ? 'Free' : '$4.99'}</span>
                  </div>
                </div>

                <Separator className="my-4" />

                <div className="flex justify-between font-semibold text-lg mb-6">
                  <span>Total</span>
                  <span data-testid="cart-total">${(total + (total >= 30 ? 0 : 4.99)).toFixed(2)}</span>
                </div>

                <Button 
                  className="w-full btn-lift" 
                  size="lg"
                  onClick={() => navigate('/checkout')}
                  data-testid="checkout-button"
                >
                  Proceed to Checkout
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>

                <p className="text-xs text-muted-foreground text-center mt-4">
                  Free shipping on orders over $30
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};
